import { create } from 'zustand';
import { IUser } from '../@shared/interfaces';
import { userService } from '../services/api/user.service';

interface AuthStore {
  // Token management
  getToken: (() => Promise<string>) | null;
  setGetToken: (fn: () => Promise<string>) => void;

  // User and profile data
  user: IUser | null;
  profile: IUser | null;
  isLoadingProfile: boolean;
  profileError: Error | null;

  // Auth actions
  setUser: (user: IUser | null) => void;
  clearUser: () => void;

  // Profile actions
  fetchProfile: () => Promise<IUser | null>;
  updateProfile: (data: Partial<IUser>) => Promise<IUser | null>;
}

export const useAuthStore = create<AuthStore>((set) => ({
  // Initial token state
  getToken: null,
  setGetToken: (fn) => set({ getToken: fn }),

  // Initial user and profile state
  user: null,
  profile: null,
  isLoadingProfile: false,
  profileError: null,

  // User management
  setUser: (user) => set({ user }),
  clearUser: () =>
    set({
      user: null,
      getToken: null,
      profile: null,
      profileError: null,
    }),

  // Profile management
  fetchProfile: async () => {
    try {
      set((state) => ({
        isLoadingProfile: true,
        // Keep any existing profile data during loading
        profile: state.profile,
        profileError: null,
      }));

      const response = await userService.getProfile();
      if (response.success && response.user) {
        set({ profile: response.user, isLoadingProfile: false });
        return response.user;
      } else {
        // Don't clear the profile on non-auth errors
        set((state) => ({
          profile: state.profile, // Keep any existing profile data
          profileError: new Error(
            response.message || 'Failed to fetch profile'
          ),
          isLoadingProfile: false,
        }));
        return null;
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error occurred';

      // Don't clear the profile on network errors
      set((state) => ({
        profile: state.profile, // Keep any existing profile data
        profileError: new Error(errorMessage),
        isLoadingProfile: false,
      }));
      console.error('Error fetching user profile:', error);
      return null;
    }
  },

  updateProfile: async (data: Partial<IUser>) => {
    try {
      set((state) => ({
        isLoadingProfile: true,
        // Keep existing profile during updates
        profile: state.profile,
        profileError: null,
      }));

      const response = await userService.updateProfile(data);
      if (response.success && response.user) {
        // Update the stored profile with the new data
        set({
          profile: response.user,
          isLoadingProfile: false,
        });
        return response.user;
      } else {
        // Don't clear profile on update error
        set((state) => ({
          profile: state.profile,
          profileError: new Error(
            response.message || 'Failed to update profile'
          ),
          isLoadingProfile: false,
        }));
        return null;
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error occurred';

      // Keep existing profile on network errors
      set((state) => ({
        profile: state.profile,
        profileError: new Error(errorMessage),
        isLoadingProfile: false,
      }));
      console.error('Error updating user profile:', error);
      return null;
    }
  },
}));
